// Import Packages
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';

// Import Material UI
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Close from '@material-ui/icons/Close';
import MoreVertIcon from '@material-ui/icons/MoreVert';

// Import Pages
import ConnectorField from '../../../home/pages/automs/newFlow/middleArea/connectorField/ConnectorField';
import FieldLabel from '../../FlowConfigurationInputs/FieldLabel/FieldLabel';
import { TextInput } from '../../FlowConfigurationInputs/TextInput/NewTextInput';

// Import Recoil
import { triggerConf } from '../../../recoil-store/recoil-state';

// Import CSS
import '../../FlowConfigurationInputs/ConditionModal/ConditionModal.css';


const label = 'CONTENT.CONFIGURATION-FORM.CONDITION.';

export default function ConditionModal({ action, automId, automVersionId, setOpen, conditionInfo, variables, setOpenVariable, handleAdd, handleUpdate, handleDelete, onClose, process, variableRender, automDetail }) {
    const { t } = useTranslation();
    const [triggerConfig, setTriggerConfig] = useRecoilState(triggerConf);

    const conditionBackup = useRef();

    const handleClose = () => {
        if (onClose) { onClose(conditionBackup.current); }
        else { setOpen(false); }
    };

    return (
        <Dialog open={true} onClose={handleClose} PaperProps={{ style: { overflowY: "visible" } }}>
            <DialogTitle>
                <div className="modal-title">
                    {handleAdd ? t(label + 'MODAL.HEADER') : t(label + 'MODAL.UPDATE')}
                    <Button data-test="cancelautom-button" onClick={handleClose}>
                        <Close />
                    </Button>
                </div>
            </DialogTitle>
            <DialogContent style={{ minWidth: 300, overflowY: "none" }}>
                <ConnectorField automDetail={automDetail} action={action} showFieldPreview={true}
                    triggerConfig={triggerConfig} triggerExpanded={true}
                    debugOpen={false} debugExpand={false}
                    onFlowPage={true}
                    setTriggerConf={setTriggerConfig} automId={automId}
                    automVersionId={automVersionId} rightLocation={false}
                    variables={variables} setOpenVariable={setOpenVariable} resizeClassName="dialog-data-table" />

                <Condition conditionInfo={conditionInfo} variables={variables} setOpenVariable={setOpenVariable} variableRender={variableRender} process={process} conditionBackup={conditionBackup} fromLibrary={action.libraryName} />
            </DialogContent>
            <DialogActions className="modal-actions">
                {handleDelete &&
                    <Button
                        onClick={handleDelete}
                        variant="outlined"
                        style={{ float: "left", backgroundColor: "#D75E5E", borderRadius: "2px", color: "#fff", width: "45%", border: "none" }}>
                        {t('CONTENT.DATATABLE.SELECTEDROWS.DELETE')}
                    </Button>}
                <Button
                    onClick={handleAdd ? handleAdd : handleUpdate}
                    variant="outlined"
                    style={{ float: "right", backgroundColor: "#46474E", borderRadius: "2px", color: "#fff", width: "45%", border: "none" }}>
                    {handleAdd ? t('CONTENT.CONFIGURATION-FORM.ADD') : t('CONTENT.CONFIGURATION-FORM.UPDATE')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export function Condition({ conditionInfo, conditionBackup, variables, variableRender, process, setOpenVariable, setField, fromLibrary }) {
    const { t } = useTranslation();
    const [tab, setTab] = useState();
    const [hideValue, setHideValue] = useState(false);
    const [operator, setOperator] = useState('eq');

    const label = 'CONTENT.CONFIGURATION-FORM.EXCEL.SEARCH.';
    const conditionLabel = 'CONTENT.CONFIGURATION-FORM.CONDITION.';
    const dataTableLabel = 'CONTENT.CONFIGURATION-FORM.DATA-TABLE.';

    const options = {
        operator: {
            data: [
                {
                    value: "eq",
                    label: `= (${t(conditionLabel + 'EQ')})`,
                },
                {
                    value: "ne",
                    label: `≠ (${t(conditionLabel + 'NE')})`,
                },
                {
                    value: "empty",
                    label: `∅ (${t(conditionLabel + 'EMPTY')})`,
                },
                {
                    value: "nempty",
                    label: t(conditionLabel + 'NEMPTY'),
                },
                {
                    value: "contains",
                    label: t(conditionLabel + 'CONTAINS'),
                }
            ],
            defaultValue: "eq",
        }
    };

    useEffect(() => {
        // Assign initial values
        if (!conditionInfo.fields.tab) {
            let v = fromLibrary === 'EXCEL' ? 'all' : 'index';
            conditionInfo.fields.tab = { value: v };
            setTab(v);
        } else {
            setTab(conditionInfo.fields.tab.value);
        }

        if (!conditionInfo.operator) {
            conditionInfo.operator = 'eq';
        } else {
            onChangeOperator(conditionInfo.operator)
        }
    }, [conditionInfo.id]);

    // Handle tab change
    const changeTab = (e) => {
        let value = e.target.value;
        conditionInfo.fields.tab = { value };
        if (value === 'all') {
            if (conditionInfo.fields.columnToSearch) {
                conditionInfo.fields.columnToSearch.isRequired = false;
            }
        }
        if (conditionInfo.fields.set) {
            conditionInfo.fields.set.value = ""
        }

        setTab(value);
    };

    // Handle tab change
    const changeColumnTab = (e) => {
        let value = e.target.value;
        conditionInfo.fields.tab = { value };
        // conditionInfo.fields.columnToSearch.value = '';

        setTab(value);
    };

    const onChangeOperator = (value) => {
        if (value === "empty" || value === "nempty") {
            setHideValue(true);
        } else {
            setHideValue(false);
        }
        conditionInfo.operator = value;
        setOperator(value)
    }

    return (
        <div>

            <div className="conf-field-container">
                <FieldLabel name={t(label + "OPERATOR")} />
                <Select value={operator} onChange={(e) => onChangeOperator(e.target.value)} variant="outlined" fullWidth>
                    {options.operator.data.map((item, itemI) =>
                        <MenuItem style={{ fontSize: 12, color: "#8F94A6", fontWeight: "800" }} key={itemI} value={item.value}>{t(item.label)}</MenuItem>
                    )}
                </Select>
            </div>
            {
                !hideValue &&
                <div className="conf-field-container">
                    <FieldLabel name={t(label + 'VALUE-TO-SEARCH')} is_required />
                    <TextInput action={conditionInfo} inputName="valueToSearch" variables={variables}
                        setOpenVariable={setOpenVariable} isRequired hideRequired displayName={t(label + 'VALUE-TO-SEARCH')} />
                </div>
            }
            {
                fromLibrary === 'EXCEL' ?
                    <div>
                        <div className="conf-field-container condition-tabs">
                            <button className={`f-s-11 ${tab === 'all' ? 'condition-active' : ''}`}
                                value="all" onClick={changeTab}>
                                {t(label + 'SEARCH-ALL-DATA')}
                            </button>
                            <button className={`f-s-11 ${tab === 'column' ? 'condition-active' : ''}`}
                                value="column" onClick={changeTab}>
                                {t(label + 'SEARCH-IN-COLUMN')}
                            </button>
                        </div>
                        {tab === 'column' &&
                            <div className="conf-field-container">
                                <FieldLabel name={t(label + 'COLUMN-TO-SEARCH')} is_required />
                                <TextInput action={conditionInfo} inputName="columnToSearch" variables={variables}
                                    setOpenVariable={setOpenVariable} isRequired hideRequired displayName={t(label + 'COLUMN-TO-SEARCH')} fieldType="string-columns" />
                                <small className="conf-field-desc"><em>{t(label + 'COLUMN-INDEX-EG')}</em></small>
                            </div>
                        }
                    </div>
                    :
                    <div>
                        <div className="conf-field-container condition-tabs">
                            <button className={`f-s-11 ${tab === 'index' ? 'condition-active' : ''}`}
                                value="index" onClick={changeColumnTab}>
                                {t(dataTableLabel + 'COLUMN-INDEX')}
                            </button>
                            <button className={`f-s-11 ${tab === 'name' ? 'condition-active' : ''}`}
                                value="name" onClick={changeColumnTab}>
                                {t(dataTableLabel + 'COLUMN-NAME')}
                            </button>
                        </div>

                        <div className="conf-field-container">
                            <FieldLabel name={t(label + 'COLUMN-TO-SEARCH')} is_required />
                            <TextInput action={conditionInfo} inputName="columnToSearch" variables={variables}
                                setOpenVariable={setOpenVariable} isRequired hideRequired displayName={t(label + 'COLUMN-TO-SEARCH')} fieldType="string-columns" />
                            <small className="conf-field-desc"><em>{t(dataTableLabel + `COLUMN-${tab?.toUpperCase()}-EG`)}</em></small>
                        </div>

                    </div>
            }

        </div>
    )
}

export function ConditionPicker({ condition, onClick, fromLibrary }) {
    const { t } = useTranslation();
    const label = 'CONTENT.CONFIGURATION-FORM.CONDITION.';
    return (
        <div className="condition-picker">
            <fieldset>
                <legend>&nbsp;<span>{t(label + condition.operator.toUpperCase())}</span>&nbsp;</legend>
                <div>
                    <div>
                        {
                            condition.operator !== "empty" && condition.operator !== "nempty" &&
                            <span className="condition-value">"{condition.fields.valueToSearch.value}"&nbsp;</span>
                        }
                        {fromLibrary === 'EXCEL' ?
                            <span className="condition-value">in {condition.fields.tab.value === "column" ? ` "${condition.fields.columnToSearch.value}" column` : ` all data`}</span> :
                            <span className="condition-value">in
                                {condition.fields.tab.value === "name" ? ` "${condition.fields.columnToSearch.value}" column` :
                                    ` ${condition.fields.columnToSearch.value} 'index'`}
                            </span>
                        }
                    </div>
                    <div>
                        <button onClick={onClick}><MoreVertIcon /></button>
                    </div>
                </div>
            </fieldset>
        </div>
    );
}